import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgIf, NgClass, NgSwitch, NgFor, KeyValuePipe, NgSwitchDefault } from '@angular/common';
import { UserService } from '../../../../chat-common/src/lib/services/user.service';
import { QuickActionsOverlayComponent } from './components/quick-actions-overlay/quick-actions-overlay.component';
import { CycleStatusComponent } from './components/cycle-status/cycle-status.component';
import { QuickActionsComponent } from './components/quick-actions/quick-actions.component';
import { LoaderComponent } from './components/loader/loader.component';
import { GptService } from './services/gpt.service';
import { TextCardComponent } from './components/chat-cards/text-card/text-card.component';
import { BehaviorSubject, combineLatest, map, Observable, ReplaySubject, startWith, take } from 'rxjs';
import { ChatMessage } from './models/chat-message';
import { SingleInputCardComponent } from './components/chat-cards/single-input-card/single-input-card.component';
import { MultiInputCardComponent } from './components/chat-cards/multi-input-card/multi-input-card.component';
import { ContentCardComponent } from './components/chat-cards/content-card/content-card.component';
import { UserInputCardComponent } from './components/chat-cards/user-input-card/user-input-card.component';
import { ChatOption } from 'projects/chat-common/src/lib/models/chat.option';
import { ActionService } from './services/action.service';
import { PeriodService } from 'projects/chat-common/src/lib/services/period.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserInfoService } from 'projects/chat-common/src/lib/services/user.info.service';
import { ChatDataService } from 'projects/chat-common/src/lib/services/chat.data.service';
import { ContentService } from './services/content.service';
import { GeneralAssistantCardComponent } from './components/chat-cards/general-assistant-card/general-assistant-card.component';
import * as moment from 'moment';
import { TemperaturePickerComponent } from './components/pickers/temperature-picker/temperature-picker.component';
import { DatePickerComponent } from './components/pickers/date-picker/date-picker.component';
import { SidemenuComponent } from './components/sidemenu/sidemenu.component';
import { ContentViewerComponent } from './components/content-viewer/content-viewer.component';
import { ContentPlayerComponent } from './components/content-player/content.player.component';
import { SubscriptionComponent } from './components/subscription/subscription.component';
import { TryPremiumComponent } from './components/try-premium/try-premium.component';
import { HttpClient } from '@angular/common/http';
import { OnboardingQuestionType, OnboardingService } from 'projects/chat-common/src/lib/services/onboarding.service';
import { OpenAIService } from './services/openai.service';
import { Period } from 'projects/chat-common/src/lib/models/period';
import { EncryptionBlockerComponent } from './components/encryption-blocker/encryption-blocker.component';
import { WellnessChartComponent } from './components/chat-graph/wellness-chart.component';
import { DesktopHeaderComponent, DesktopHeaderSection } from './components/desktop-header/desktop-header.component';
import { DesktopFooterComponent } from './components/desktop-footer/desktop-footer.component';
import { ProfileOnboardingComponent } from './components/profile-onboarding/profile-onboarding.component';
import { ProfileOnboardingService } from './components/profile-onboarding/profile-onboarding.service';
import { ProfileAnswer, ProfileQuestion } from './components/profile-onboarding/profile-question.model';

@Component({
  selector: 'app-chat-gpt',
  standalone: true,
  imports: [
    FormsModule,
    AsyncPipe,
    NgIf,
    QuickActionsOverlayComponent,
    CycleStatusComponent,
    QuickActionsComponent,
    LoaderComponent,
    TextCardComponent,
    SingleInputCardComponent,
    MultiInputCardComponent,
    ContentCardComponent,
    UserInputCardComponent,
    GeneralAssistantCardComponent,
    ContentPlayerComponent,
    NgFor,
    KeyValuePipe,
    NgClass,
    SidemenuComponent,
    ContentViewerComponent,
    ContentPlayerComponent,
    SubscriptionComponent,
    TryPremiumComponent,
    EncryptionBlockerComponent,
    WellnessChartComponent,
    DesktopHeaderComponent,
    DesktopFooterComponent,
    ProfileOnboardingComponent,
  ],
  templateUrl: './chat-gpt.component.html',
  styleUrl: './chat-gpt.component.scss',
})
export class ChatGptComponent implements OnInit, OnDestroy {

  dayLimitReached = false;
  showSubscription = false;
  showProfileOnboarding = false;
  profileQuestions: ProfileQuestion[] = [];
  isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  private destroyRef = inject(DestroyRef);
  showQuickActionsOverlay = false;
  showLoader: boolean = true;
  isMenuOpen = false;
  isOnboardingOpen = false;
  isInputOpen = false;
  isContentViewerOpen = false;

  weblinkUrl: any = null;
  cyclePhase: { state: string, day?: string | null, trimester?: string } = { state: '', day: null };

  @ViewChild('chatContainer') private chatContainer: ElementRef
  @ViewChild('bottomMarker') bottomMarker!: ElementRef;

  quickActions: Array<ChatOption> = []
  messageText: string = '';
  messages$: Observable<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  onboardingMessages$: Observable<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  selectedQuickAction: boolean = false;
  userName: string | null = null;
  phaseClass = 'phase--luteal';
  graphType: "sleep" | "activity" | "steps" | null = null;
  lastMessageId: string;
  recognition: any;
  isRecording = false;
  showScrollHistoryButton = false;
  multiSelectOptions: { active: boolean, options: Record<string, ChatOption[]> } = { active: false, options: {} };

  public selectedQuickSubAction: { option: ChatOption, logOptions: { id: number, label: string, value: string, icon: string }[] | null } | null = null;
  currentMessages: ChatMessage[] | null = null;

  triggerQuickActionChange = new EventEmitter<void>();
  triggerSelectedAction = new ReplaySubject<ChatOption | null>(1);

  missingOnboardingData: OnboardingQuestionType[] = [];
  currentOnboardingData?: OnboardingQuestionType = null;
  currentOnboardingOption: ChatOption | null = null;
  public displayedAllMessages$ = new BehaviorSubject<Record<string, ChatMessage[]>>({});


  totalDaysAvailable = 0;

  public userData: { age: number, usageReason: string, hasPeriod: boolean, isPregnant: boolean, isPostpartum: boolean } = {
    age: 0, usageReason: 'trackMyCycle', hasPeriod: false, isPregnant: false, isPostpartum: false
  }

  showPlayer = false;
  playerSrc: string | null = null;
  playerImage: string | null = null;

  public displayedChatMessages$ = new BehaviorSubject<Record<string, ChatMessage[]>>({});
  private daysBackToShow = 0;
  public isEncrypted: boolean = false;
  private onboardingTriggered = false;

  showOnboardingOptions$ = this.displayedChatMessages$.pipe(
    map(messages => messages.value.length === 0)
  );

  constructor(
    protected userService: UserService,
    private gptService: GptService,
    private actionService: ActionService,
    private periodService: PeriodService,
    private userInfoService: UserInfoService,
    private userDataService: ChatDataService,
    private contentService: ContentService,
    private onboardingService: OnboardingService,
    private openaiService: OpenAIService,
    private ngZone: NgZone,
    private http: HttpClient,
    private profileOnboardingService: ProfileOnboardingService,
  ) {
    this.contentService.loadInitialContent();
    this.constructSpeechRecording();
    this.observeEncryption();
  }

  observeEncryption() {
    this.userInfoService.isDataEncrypted$
      .pipe(take(1))
      .subscribe((encrypted) => {
        this.isEncrypted = encrypted;
        if (!encrypted && !this.onboardingTriggered) {
          this.onboardingTriggered = true;
          this.observeOnboarding();
        }
      });
  }

  shouldDisableInput(): boolean {
     if (this.currentOnboardingData && this.currentOnboardingData == OnboardingQuestionType.usageReason) {
      return true;
     }

     return false;
  }

  async ngOnInit(): Promise<void> {
    await this.loadContent();
    this.totalDaysAvailable = await this.gptService.getTotalDaysAvailable();
    this.constructActions();
    this.calculateCyclePhase();
    this.subscribeToReproductiveData();
    this.getUserName();
    this.getUserData();


    combineLatest([
      this.gptService.chatMessages$.pipe(startWith([])),
      this.gptService.chatOnboardingMessages$.pipe(startWith([]))
    ]).subscribe(([chatMsgs, onboardingMsgs]) => {
      const normalizeDate = (m: ChatMessage) => {
        if (!m.date) m.date = new Date();
        return m;
      };
      const all = [...chatMsgs.map(normalizeDate), ...onboardingMsgs.map(normalizeDate)];
      this.applyMessageFilter(all, this.displayedAllMessages$);
    });
  }


  private async getIntroMessages() {
    this.showLoader = true;
    this.gptService.saveChatDailyInfo(new Date());
  }

  private applyMessageFilter(messages: ChatMessage[], target$: BehaviorSubject<Record<string, ChatMessage[]>>) {
    const grouped: Record<string, ChatMessage[]> = {};


    messages.forEach(m => {
      if (!m?.date) return;
      const key = moment(m.date).startOf('day').format('YYYY-MM-DD');
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    });


    Object.keys(grouped).forEach(k => {
      grouped[k] = grouped[k].sort((a, b) =>
        moment(a.date).valueOf() - moment(b.date).valueOf()
      );
    });


    const sortedDays = Object.keys(grouped).sort((a, b) =>
      moment(b).valueOf() - moment(a).valueOf()
    );


    const daysToShow = this.daysBackToShow + 1;
    const limited: Record<string, ChatMessage[]> = {};
    sortedDays.slice(0, daysToShow).forEach(day => {
      limited[day] = grouped[day];
    });

    target$.next(limited);
  }


  loadMoreMessages() {
    if (!this.canLoadMore()) return;

    const container = this.chatContainer.nativeElement;

    const firstVisible = this.getFirstVisibleMessage(container);
    const prevId = firstVisible?.dataset['msgId'];

    this.daysBackToShow++;

    combineLatest([
      this.gptService.chatMessages$.pipe(take(1)),
      this.gptService.chatOnboardingMessages$.pipe(take(1))
    ]).subscribe(([chatMsgs, onboardingMsgs]) => {
      const all = [...chatMsgs, ...onboardingMsgs];
      this.applyMessageFilter(all, this.displayedAllMessages$);

      if (prevId) {
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
          const sameEl = container.querySelector(
            `[data-msg-id="${prevId}"]`
          ) as HTMLElement;

          if (sameEl) {
            const prevSibling = sameEl.previousElementSibling as HTMLElement;

            if (prevSibling) {
              prevSibling.scrollIntoView({ block: 'start' });
            } else {
              sameEl.scrollIntoView({ block: 'start' });
            }
          }
        });
      }
    });

    this.showScrollHistoryButton = false;
  }

  getScrollableParent(el: HTMLElement): HTMLElement | null {
    let parent: HTMLElement | null = el;
    while (parent) {
      const hasScrollableContent = parent.scrollHeight > parent.clientHeight;
      const overflowY = window.getComputedStyle(parent).overflowY;
      const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
      if (hasScrollableContent && isScrollable) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }



  trackByDay(index: number, day: { key: string, value: ChatMessage[] }) {
    return day.key;
  }

  trackByMessage(index: number, msg: ChatMessage) {
    return msg.id;
  }


  private canLoadMore(): boolean {
    return this.daysBackToShow < this.totalDaysAvailable;
  }

  dateSort = (a: any, b: any) => a.key > b.key ? 1 : -1;

  formatDay(dateString: string): string {
    const day = moment(dateString).startOf('day');
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    if (day.isSame(today)) return 'Today';
    if (day.isSame(yesterday)) return 'Yesterday';
    return day.format('YYYY-MM-DD');
  }

  shouldShowDivider(totalDays: number): boolean {
    if (totalDays === 1) return false;
    return true;
  }

  async findMissingOnboardingData() {
    this.missingOnboardingData = await this.onboardingService.getInitialMissingOnboardingData();
    if (this.missingOnboardingData.length) {
      this.showLoader = false;
      this.currentOnboardingData = this.missingOnboardingData[0];
      const [ref] = await this.gptService.sendOnboardingMessage(this.onboardingService.currentMessage(this.currentOnboardingData));
      // Options are only shown on the message with this id, which Firestore assigns
      this.lastMessageId = ref.id;
    } else {
      this.missingOnboardingData = null;
      this.currentOnboardingOption = null;
      this.lastMessageId = null;
      this.showLoader = true;
      let finishMessages = this.onboardingService.finishMessages();
      for (const msg of finishMessages) {
        await this.gptService.sendOnboardingMessage(msg);
      }
      this.onboardingFinished();
    }
  }

  async onboardingSendMessage() {
    let type = this.currentOnboardingData;
    this.gptService.sendOnboardingMessage(new ChatMessage(this.messageText));
    this.showLoader = true;
    if (this.currentOnboardingOption?.action == 'next') {
      this.messageText = '';
      await this.findMissingOnboardingData();
      this.currentOnboardingOption = null;
      return;
    }
    switch (type) {
      case OnboardingQuestionType.name:
        await this.onboardingService.saveUserName(this.messageText);
        await this.getUserName();
        break;
      case OnboardingQuestionType.usageReason:
        await this.onboardingService.saveUsageReason(this.currentOnboardingOption.action);
        break;
      case OnboardingQuestionType.age:
        let dateOfBirth = await this.openaiService.requestDateOfBirth(this.messageText);
        await this.onboardingService.saveDateOfBirth(dateOfBirth);
        break;
      case OnboardingQuestionType.lastPeriod:
        let lastPeriodStartDate = await this.openaiService.requestDateOfPeriod(this.messageText);
        const startDate = new Date(lastPeriodStartDate);
        const endDate = this.periodService.calculateEndDate(startDate);
        let endDateString = endDate == null ? null : moment(endDate).format('YYYY-MM-DD');
        let period = new Period(lastPeriodStartDate, endDateString);
        await this.periodService.addPeriod(period);
        break;
      case OnboardingQuestionType.dueDate:
        await this.openaiService.requestDateOfPeriod(this.messageText);
        break;
    }
    this.messageText = '';
    await this.findMissingOnboardingData()
  }

  async observeOnboarding() {
    this.gptService.getOnboardingChat();
    this.onboardingMessages$ = this.gptService.chatOnboardingMessages$;

    const onboardingMessages = await this.gptService.getOnboardingMessagesOnce();
    const messageHistory = await this.gptService.getMessagesOnce();

    const isOnboardingFinished =
      onboardingMessages?.some(m => m.message === "We’re ready to kick off your first session together.") ?? false;

    const hasHistory = !!(messageHistory && messageHistory.length);
    const hasOnboarding = !!(onboardingMessages && onboardingMessages.length);

    const lastMessage = onboardingMessages?.[onboardingMessages.length - 1];
    const lastIsActionNext = lastMessage?.answer?.action === 'next';
    const hasLastNonNextMessage =
      hasOnboarding && !lastIsActionNext && lastMessage?.message !== "We’re ready to kick off your first session together.";

    const hasMissingData = await this.onboardingService.missingOnboardingData();

    if (hasHistory) {
      const today = new Date().toDateString();

      const messagesToday = messageHistory.some(m => {
        const msgDate = m.date.toDate().toDateString();
        return msgDate === today;
      })

      if (messagesToday) {
        this.subscribeToMessages();
      } else {
        this.getIntroAndSubscribeToMessages();
      }

    } else if (isOnboardingFinished || !hasMissingData) {
      this.onboardingFinished();

    } else if (!isOnboardingFinished && hasLastNonNextMessage) {
      await this.findMissingOnboardingData();

    } else {
      const introMessages = await this.onboardingService.generalOnboardingMessages();
      for (const msg of introMessages) {
        await this.gptService.sendOnboardingMessage(msg);
      }
      await this.findMissingOnboardingData();
    }

    this.onboardingMessages$.subscribe(async messages => {
      if (hasHistory) return;

      if (!messages || (messages && messages.length == 0)) {
        this.showLoader = true;
        return;
      }

      if (messages && messages[messages.length - 1]?.source !== 'user') {
        this.showLoader = false;
      }


      setTimeout(() => this.scrollToBottom('auto'), 0);

    })
  }

  onboardingFinished() {
    this.getIntroAndSubscribeToMessages();
  }

  getIntroAndSubscribeToMessages() {
    this.showLoader = true;
    this.getIntroMessages();
    this.subscribeToMessages();
  }

  subscribeToMessages() {
    this.gptService.getChat();
    this.userDataService.generateUserDataMessage();
    this.messages$ = this.gptService.chatMessages$;
    this.messages$.subscribe(messages => {
      if (!messages || (messages && messages.length == 0)) {
        this.showLoader = true;
        return;
      }
      if (messages && messages[messages.length - 1]?.source !== 'user') {
        this.showLoader = false;
        let lastMessage = messages[messages.length - 1]
        this.lastMessageId = lastMessage?.id ?? null;
        /*const lastIsIntro = lastMessage?.answer?.cta === 'INTRO' && this.shouldSendIntro;
        const introCount = (messages ?? []).reduce(
          (n, m) => n + (m?.answer?.cta === 'INTRO' ? 1 : 0), 0
        );
        if (lastIsIntro) {
          let timeout = introCount >= 3 ? 10000 : 1000;
          setTimeout(async () => {
            let isUserInterrupted = await this.gptService.hasUserInterrupted();
            if (!isUserInterrupted) {
              //this.gptService.sendMockMessage();
            }
          }, timeout);
        }*/
      }
      if (!this.userService.user.subscribed) {
        const today = moment().startOf('day')
        const assistantMessagesToday = messages.filter(message => {
          return message.source === 'assistant' && moment(message.date).isSameOrAfter(today)
        }).length
        console.log(`Free Messages today: ${assistantMessagesToday}`)
        if (assistantMessagesToday >= 13) {
          this.dayLimitReached = true;
          this.showSubscription = true;
        }
      }
      setTimeout(() => this.scrollToBottom('auto'), 0);
    })
  }

  onHeaderSectionSelect(section: DesktopHeaderSection) {
    if (section === 'settings') {
      this.openMenu();
    }
  }

  openMenu() { this.isMenuOpen = true; document.body.style.overflow = 'hidden'; }
  closeMenu() { this.isMenuOpen = false; document.body.style.overflow = ''; }
  openPlayer(src: string) { this.playerSrc = src; this.showPlayer = true; }
  closePlayer() { this.showPlayer = false; this.playerSrc = null; }
  ngOnDestroy(): void {
    // The open menu locks page scroll; release it when leaving the chat (e.g. to My data)
    document.body.style.overflow = '';
  }

  private scrollToBottom(behavior: 'smooth' | 'auto'): void {
    if (this.bottomMarker) {
      this.bottomMarker.nativeElement.scrollIntoView({ behavior: behavior, block: 'end' });
    }
  }

  private getFirstVisibleMessage(container: HTMLElement): HTMLElement | null {
    const messages = Array.from(container.querySelectorAll('[data-msg-id]')) as HTMLElement[];
    for (const el of messages) {
      const rect = el.getBoundingClientRect();
      if (rect.top >= 0) {
        return el;
      }
    }
    return null;
  }



  selectQuickSubAction($event: { option: ChatOption, logOptions: { id: number, label: string, value: string, icon: string }[] | null } | null) {
    this.selectedQuickSubAction = $event;
  }

  constructSpeechRecording() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.lang = 'en-US';
      this.recognition.continuous = true;
      this.recognition.interimResults = false;
      this.recognition.onresult = (event: any) => {
        const transcript = event.results[event.results.length - 1][0].transcript;
        this.messageText = this.messageText ? this.messageText + ' ' + transcript : transcript;
      };
      this.recognition.onerror = () => { this.stopVoiceRecording(); };
      this.recognition.onend = () => { if (this.isRecording) this.recognition.start(); };
    }
  }
  startVoiceRecording() { if (!this.recognition) return; this.isRecording = true; this.recognition.start(); }
  stopVoiceRecording() { if (!this.recognition) return; this.isRecording = false; this.recognition.stop(); }

  getPhaseClass(a?: string): string {
    let k = (a ?? this.cyclePhase?.state ?? '').toLowerCase();
    if (k === 'no data' || k === 'No data' || !k) k = 'luteal';
    const map: Record<string, string> = {
      follicular: 'phase--follicular',
      period: 'phase--period',
      fertile: 'phase--fertile',
      ovulation: 'phase--fertile',
      luteal: 'phase--luteal',
      late: 'phase--luteal',
      pregnancy: 'phase--preg-postpartum',
      postpartum: 'phase--preg-postpartum'
    };
    return map[k] ?? 'phase--luteal';
  }

  async sendQuickMessage(option: ChatOption) {
    let graphAction = this.actionService.graphAction(option);
    if (graphAction) {
      this.graphType = graphAction;
      return;
    }

    let isOverlayAction = this.actionService.isOverlayAction(option);
    if (isOverlayAction) {
      this.toggleQuickActionOverlay(true);
      this.triggerSelectedAction.next(option);
    } else {
      await this.actionService.handleOption(option);
      if (this.actionService.shouldShowLoader(option)) {
        this.showLoader = true;
      }
    }
  }

  async calculateCyclePhase() {
    this.cyclePhase = await this.periodService.calculatePhase();
    this.phaseClass = this.getPhaseClass(this.cyclePhase?.state);
  }

  async getUserName() { this.userName = await this.userInfoService.getUserName(); }

  async getUserData() {
    let userInfoHealthReason = (await this.userInfoService.getReproductiveHealth())?.usageReason ?? 'trackMyCycle';
    let userInfoDateOfBirth = (await this.userInfoService.getUserProfile())?.dateOfBirth;
    this.userData.usageReason = userInfoHealthReason

    if (userInfoDateOfBirth) {
      const age = moment(userInfoDateOfBirth);
      const userAge = moment().diff(age, 'years');
      this.userData.age = userAge;
    }
  }

  subscribeToReproductiveData(): void {
    combineLatest([
      this.periodService.periodChanges$.pipe(startWith(null)),
      this.periodService.pregnancyChanges$.pipe(startWith(null))
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async ([period, pregnancy]) => {
        await this.calculateCyclePhase();
        this.constructPeriodUserData(this.cyclePhase.state);
      });
  }

  constructPeriodUserData(phase?: string) {
    if (phase == 'Period') { this.userData.hasPeriod = true; this.userData.isPregnant = false; this.userData.isPostpartum = false; }
    if (phase == 'Pregnancy') { this.userData.hasPeriod = false; this.userData.isPregnant = true; }
    if (phase == 'Postpartum') { this.userData.isPregnant = false; this.userData.isPostpartum = false; }
  }

  constructActions(): void {
    combineLatest<[boolean, boolean, boolean, boolean]>([
      this.periodService.hasActivePeriod$ as Observable<boolean>,
      this.periodService.hasActivePregnancy$ as Observable<boolean>,
      this.periodService.hasSleepData$ as Observable<boolean>,
      this.userInfoService.hasDevices$ as Observable<boolean>,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasPeriod, hasPregnancy, hasSleep, hasDevices]) => {
        const actions: ChatOption[] = [
          new ChatOption('showQuickView', 'All'),
          ...(!hasDevices ? [new ChatOption('purchaseIVY', 'Purchase IVY+')] : []),
          new ChatOption('symptoms', 'Log symptoms'),
          hasPeriod ? new ChatOption('stopPeriod', 'Stop period') : new ChatOption('startPeriod', 'Start period'),
          new ChatOption('analyseMyData', 'Analyse my data'),
          ...(hasDevices ? [new ChatOption('showSleep', 'Show sleep trend'), new ChatOption('showActivity', 'Show activity trend'), new ChatOption('showSteps', 'Show steps trend')] : []),
          new ChatOption('downloadReport', 'Download report'),
          ...(hasSleep ? [new ChatOption('analyseSleep', 'Analyse my sleep data')] : []),
          ...(hasPregnancy
            ? [new ChatOption('pregnancyOverview', 'Pregnancy overview')]
            : [new ChatOption('fertilityPrediction', 'Get fertility prediction'), new ChatOption('cycleAnalysis', 'Get cycle analysis')]),
        ];

        this.quickActions = actions;
        setTimeout(() => this.scrollToBottom('smooth'), 0)
      });
  }

  async sendMessage() {
    if (this.missingOnboardingData != null && this.missingOnboardingData.length || this.currentOnboardingOption) {
      this.onboardingSendMessage();
      return;
    }
    if (this.messageText.trim() && await this.shouldCompleteProfileFirst()) {
      // The typed message stays in the input so it can be sent once the profile is done
      return;
    }
    this.showLoader = true;
    let shouldSkipSendingMessage = false;
    if (this.selectedQuickSubAction != null) {
      this.sendAction(this.selectedQuickSubAction);
      this.messageText = '';
      return;
    }
    if (this.multiSelectOptions.active) {
      const lastId = this.lastMessageId;
      const lastOptions = this.multiSelectOptions.options[lastId] ?? [];
      if (lastOptions.length > 0) {
        shouldSkipSendingMessage = lastOptions.some(opt => this.actionService.shouldSkipSendingMessage(opt));
        await this.actionService.handleOptions(lastOptions);
      }
      this.multiSelectOptions.active = false;
    }
    if (shouldSkipSendingMessage) {
      this.messageText = '';
    }
    if (!!this.messageText.trim() && !shouldSkipSendingMessage) {
      this.gptService.sendMessage(new ChatMessage(this.messageText)).then(() => {
        this.messageText = '';
      });
    }
  }

  /** Users missing mandatory profile data get a few free messages, then have to complete it to keep chatting. */
  private async shouldCompleteProfileFirst(): Promise<boolean> {
    if (this.gptService.countSentUserMessages() < ProfileOnboardingService.FREE_MESSAGES) {
      return false;
    }
    const missing = await this.profileOnboardingService.getMissingQuestions();
    if (!missing.length) {
      return false;
    }
    this.profileQuestions = missing;
    this.showProfileOnboarding = true;
    return true;
  }

  onProfileAnswer(answer: ProfileAnswer) {
    this.profileOnboardingService.saveAnswer(answer);
  }

  onMultiselect($event: { option: ChatOption; id: string }) {
    this.multiSelectOptions.active = $event.id === this.lastMessageId;
    const currentOptions = this.multiSelectOptions.options[$event.id] ?? [];
    const exists = currentOptions.some(o => o.display === $event.option.display);
    if (!exists) {
      this.multiSelectOptions.options[$event.id] = [...currentOptions, $event.option];
    }
    if ($event.id === this.lastMessageId) {
      this.messageText = this.multiSelectOptions.options[$event.id].map(o => o.display).join(' ');
    }
  }

  onDeselectOption($event: { option: ChatOption; id: string }) {
    this.multiSelectOptions.active = $event.id === this.lastMessageId;
    const currentOptions = this.multiSelectOptions.options[$event.id] ?? [];
    this.multiSelectOptions.options[$event.id] = currentOptions.filter(o => o.display !== $event.option.display);
    if ($event.id === this.lastMessageId) {
      this.messageText = this.multiSelectOptions.options[$event.id].map(o => o.display).join(' ');
    }
  }

  async onSingleSelectOnboarding($event: ChatOption) {
    this.currentOnboardingOption = $event;
    this.messageText = $event.display;
    this.onboardingSendMessage();
  }

  async onSingleSelect($event: ChatOption) {
    // Onboarding and chat messages render through the same card, so route answers to onboarding questions here
    if (this.missingOnboardingData?.length) {
      this.onSingleSelectOnboarding($event);
      return;
    }
    let shouldSkipSendingMessage = this.actionService.shouldSkipSendingMessage($event);
    let graphType = this.actionService.graphAction($event);
    if (graphType) {
      this.graphType = graphType;
      // TODO: Add mock message, give graphs range (for now: weekly/not-null)
      return;
    }

    await this.actionService.handleOption($event);
    this.showLoader = true;
    if (!shouldSkipSendingMessage) {
      this.messageText = $event.display;
      this.sendMessage();
    } else {
      this.messageText = '';
    }
  }

  toggleQuickActionOverlay(preselect?: boolean) {
    if (this.showQuickActionsOverlay) {
      this.triggerQuickActionChange.emit();
      return;
    }
    this.showQuickActionsOverlay = !this.showQuickActionsOverlay;
    this.selectedQuickAction = preselect ?? false;
    this.selectedQuickSubAction = null;
  }

  quickActionOverlayTriggerResponse(preselect?: boolean) {
    this.showQuickActionsOverlay = !this.showQuickActionsOverlay;
    this.selectedQuickAction = preselect ?? false;
    this.selectedQuickSubAction = null;
  }

  sendAction($event: { option: ChatOption, logOptions: { id: number, label: string, value: string, icon: string }[] | null } | null) {
    let loggedData = null;
    let displayData = null;
    if ($event?.logOptions?.length) {
      loggedData = $event.logOptions.map((l) => l.value).join(',');
      displayData = $event.logOptions.map((l) => l.label.toLowerCase()).join(', ')
    }
    let shouldShowLoader = this.actionService.shouldShowLoader($event.option);
    let shouldMockResponse = this.actionService.shouldShowMockResponse($event.option.action);
    if (loggedData) {
      this.actionService.handleOption({ action: `${$event.option.action}#${loggedData}`, display: displayData ?? this.messageText });
    }
    this.actionService.handleOption({ action: $event.option.action, display: displayData ?? this.messageText });
    if (shouldShowLoader) {
      this.showLoader = true;
    }
    let messageToMock = $event.option.action == "downloadReport" ? ChatMessage.mockDownloadReportResponse() : ChatMessage.mockLogResponse(displayData ?? this.messageText);
    if (shouldMockResponse) this.gptService.sendAssistantMessage(messageToMock);
    this.quickActionOverlayTriggerResponse()
  }

  showContent($event: { url: string, image?: string }) {
    if ($event.url.includes('article2.')) {
      this.weblinkUrl = $event.url;
      this.isContentViewerOpen = true;
      document.body.style.overflow = 'hidden';
    } else {
      this.playerSrc = $event.url;
      this.showPlayer = true;
      this.playerImage = $event.image;
      this.openPlayer($event.url);
    }
  }

  closeContentViewer() {
    this.isContentViewerOpen = false;
    this.weblinkUrl = null;
    document.body.style.overflow = '';
  }

  showMoreContent($event: any) {
    this.showLoader = true;
    this.gptService.sendMessage(new ChatMessage('I’d like to see more content like these ones'));
  }


  onUserDrag(event: TouchEvent) {
    const container = event.currentTarget as HTMLElement;
    const touch = event.touches[0];

    if (container.scrollTop <= 0 && touch.clientY > 50 && this.canLoadMore()) {
      this.showScrollHistoryButton = true;
    }
  }


  onUserScroll(event: WheelEvent) {
    const container = event.currentTarget as HTMLElement;

    if (container.scrollTop <= 0 && event.deltaY < 0 && this.canLoadMore()) {
      this.showScrollHistoryButton = true;
    }
    if (event.deltaY > 0) {
      this.showScrollHistoryButton = false;
    }
  }


  async loadContent() { await this.contentService.loadInitialContent() }
}
